import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n/translations';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Home, Loader2, Globe, ArrowLeft } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const RegisterPage = () => {
  const { register, language, setLanguage } = useAuth();
  const { t } = useTranslation(language);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      await register(name, email, password, 'owner', language, phone);
      toast.success(t('registerSuccess'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('registerError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img
          src="https://images.unsplash.com/photo-1710883734891-93709398496d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsdXh1cnklMjBob21lJTIwaW50ZXJpb3IlMjBzdW5ueXxlbnwwfHx8fDE3NzE0OTgxMjJ8MA&ixlib=rb-4.1.0&q=85"
          alt="Bedroom with mountain view"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/40" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h2 className="font-heading text-4xl font-bold mb-4">
            {language === 'fr' ? 'Rejoignez notre communauté' : 'Join our community'}
          </h2>
          <p className="text-lg text-white/80 max-w-md">
            {language === 'fr' 
              ? 'Créez votre compte et commencez à gérer vos propriétés en toute sérénité'
              : 'Create your account and start managing your properties with peace of mind'
            }
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col">
        {/* Home & Language switcher */}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <Link to="/">
            <Button variant="ghost" size="sm" data-testid="register-home-btn">
              <ArrowLeft className="h-4 w-4 mr-1" />
              {language === 'fr' ? 'Accueil' : 'Home'}
            </Button>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="register-language-dropdown">
                <Globe className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setLanguage('en')} data-testid="register-lang-en">
                English
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setLanguage('fr')} data-testid="register-lang-fr">
                Français
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex-1 flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 mb-8">
              <img src="/logo.png" alt="3+ Conciergerie" className="h-12 w-12 object-contain" />
              <span className="font-heading font-bold text-2xl text-foreground">Conciergerie</span>
            </Link>

            <Card className="border-0 shadow-soft">
              <CardHeader className="space-y-1 pb-4">
                <CardTitle className="font-heading text-2xl">{t('register')}</CardTitle>
                <CardDescription>
                  {language === 'fr' 
                    ? 'Créez votre compte propriétaire pour commencer'
                    : 'Create your owner account to get started'
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('name')} *</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder={language === 'fr' ? 'Votre nom' : 'Your name'}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      data-testid="register-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t('email')} *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="register-email-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">{language === 'fr' ? 'Numéro de téléphone' : 'Phone Number'} *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="06 12 34 56 78"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      data-testid="register-phone-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">{t('password')} *</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="********"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      data-testid="register-password-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">{t('confirmPassword')} *</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="********"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      data-testid="register-confirm-password-input"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary"
                    disabled={loading}
                    data-testid="register-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('loading')}
                      </>
                    ) : (
                      t('register')
                    )}
                  </Button>
                </form>

                <div className="mt-6 text-center text-sm">
                  <span className="text-muted-foreground">{t('hasAccount')} </span>
                  <Link 
                    to="/login" 
                    className="text-primary hover:underline font-medium"
                    data-testid="login-link"
                  >
                    {t('login')}
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
